import { useCallback, useEffect, useState } from "react";
import apiClient from "../utils/apiClient";

/**
 * Server-side pagination for any paginated list endpoint.
 *
 * The endpoint is expected to return the pagination envelope
 *   { metadata: { page, page_size, total, total_pages, has_more, sort, order },
 *     records: [...] }
 * (a bare array is tolerated as a graceful fallback for a non-paginated route).
 *
 * `page` is 0-based so it drops straight into MUI <TablePagination/>; it is sent
 * to the API as the 1-based `page` query param. Changing page size / sort /
 * order / search resets back to the first page. An optional poll refreshes the
 * current page in place without a spinner.
 */
export default function usePaginatedList(
  endpoint,
  {
    initialPageSize = 25,
    initialSort = null,
    initialOrder = "desc",
    pollMs = 0,
    searchDebounceMs = 350,
    extraParams = {},
    initialQuery = "",
  } = {},
) {
  const [page, setPage] = useState(0); // 0-based (MUI convention)
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState(initialSort);
  const [order, setOrder] = useState(initialOrder);
  // initialQuery seeds the search box (e.g. a ?q= deep link) without a
  // debounce round-trip.
  const [q, setQ] = useState(initialQuery);
  const [debouncedQ, setDebouncedQ] = useState(initialQuery.trim());

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), searchDebounceMs);
    return () => clearTimeout(t);
  }, [q, searchDebounceMs]);

  // Stable string key for the extraParams object so effects/callbacks below
  // can depend on its *content* rather than its reference — callers commonly
  // pass an inline object literal, which is a new reference every render.
  const extraParamsKey = JSON.stringify(extraParams || {});

  // A narrower/wider result set, a different ordering, or different
  // extraParams (e.g. viewing a different user's data) invalidates the
  // current page index — jump back to the first page so the user isn't
  // stranded on an empty tail page.
  useEffect(() => {
    setPage(0);
  }, [pageSize, sort, order, debouncedQ, extraParamsKey]);

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("page_size", String(pageSize));
    if (sort) params.set("sort", sort);
    if (order) params.set("order", order);
    if (debouncedQ) params.set("q", debouncedQ);
    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return `${endpoint}?${params.toString()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extraParamsKey mirrors extraParams' content
  }, [endpoint, page, pageSize, sort, order, debouncedQ, extraParamsKey]);

  const fetchPage = useCallback(
    async ({ showSpinner = true } = {}) => {
      try {
        if (showSpinner) setLoading(true);
        setError(null);
        const res = await apiClient.get(buildUrl());
        if (Array.isArray(res)) {
          setRecords(res);
          setTotal(res.length);
        } else {
          setRecords(Array.isArray(res?.records) ? res.records : []);
          setTotal(Number(res?.metadata?.total ?? 0));
        }
      } catch (err) {
        console.error(`usePaginatedList(${endpoint}) failed:`, err);
        setError(err);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [buildUrl, endpoint],
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    if (!pollMs) return undefined;
    const t = setInterval(() => fetchPage({ showSpinner: false }), pollMs);
    return () => clearInterval(t);
  }, [pollMs, fetchPage]);

  return {
    records,
    total,
    loading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    order,
    setOrder,
    q,
    setQ,
    refresh: () => fetchPage({ showSpinner: false }),
  };
}
